import {Vector2, Vector3} from "three";
import { PointerTracker } from "./pointer-tracker";
import { Rect, RectConstructor } from "../../primitives/rect";
import { generateUUID } from "three/src/math/MathUtils.js";
import {PointerResult} from "./i-pointer-result";


type TouchFunctionCallback = (pointer: PointerTracker) => boolean;
type MoveFunctionCallback = (amount: Vector2) => boolean;
type ScaleFunctionCallback = (amount: number, event: WheelEvent | null) => boolean;
type AllEventsFunctionCallback = () => void;

export class TouchPrimitive extends Rect {
  tp_children: Map<string, TouchPrimitive> = new Map();
  parent: TouchPrimitive = null;
  pointers: Map<string, Vector2> = new Map();

  __moveFuncs: MoveFunctionCallback[] = [];
  __scaleFuncs: ScaleFunctionCallback[] = [];

  __contextMenuFuncs: TouchFunctionCallback[] = [];

  __pressedFuncs: TouchFunctionCallback[] = [];
  __releasedFuncs: TouchFunctionCallback[] = [];
  __hoverFuncs: TouchFunctionCallback[] = [];
  __hoverNearFuncs: TouchFunctionCallback[] = [];

  __enteredFuncs: TouchFunctionCallback[] = [];
  __exitedFuncs: TouchFunctionCallback[] = [];

  __allFuncs: AllEventsFunctionCallback[] = [];

  __enteredOnce = false;
  __exitedOnce = false;

  __moveStartPos: Vector2;
  __oldMovePos: Vector2;
  __scaleStartPos: number;
  __oldScalePos: number;

  __debugInputEvents: boolean = false;

  showPointerCursor: boolean = false;

  constructor(config?: RectConstructor) {
    let rConfig: RectConstructor = { left: 0, right: 0, top: 0, bottom: 0 };
    if (config) {
      rConfig = config;
    }
    super(rConfig);
    if (config?.id) {
      this.id = config.id;
    } else {
      this.id = generateUUID();
    }
  }

  onAllEvents(func: AllEventsFunctionCallback) {
    this.__allFuncs.push(func);
  }


  onContextMenu(func: TouchFunctionCallback) {
    this.__contextMenuFuncs.push(func);
  }

  onPressed(func: TouchFunctionCallback) {
    this.__pressedFuncs.push(func);
  }
  onReleased(func: TouchFunctionCallback) {
    this.__releasedFuncs.push(func);
  }
  onHover(func: TouchFunctionCallback) {
    this.__hoverFuncs.push(func);
  }
  onHoverNear(func: TouchFunctionCallback) {
    this.__hoverNearFuncs.push(func);
  }

  onEntered(func: TouchFunctionCallback) {
    this.__enteredFuncs.push(func);
  }
  onExited(func: TouchFunctionCallback) {
    this.__exitedFuncs.push(func);
  }

  addTouchChild(tp: TouchPrimitive) {
    this.tp_children.set(tp.id, tp);
    tp.parent = this;
  }

  removeFromParent() {
    if (this.parent) {
      this.parent.removeChild(this.id);
    }
  }

  removeChild(id: string) {
    this.tp_children.delete(id);
  }

  onMove(func: MoveFunctionCallback) {
    this.__moveFuncs.push(func);
  }
  onScale(func: ScaleFunctionCallback) {
    this.__scaleFuncs.push(func);
  }

  __allFunc() {
    if (this.__allFuncs.length === 0) {
      return false;
    }
    this.__allFuncs.forEach((func) => {
      func();
    });
    return false;
  }

  __move(amount: Vector2) {
    if (this.__moveFuncs.length === 0) {
      this.__allFunc();
      return false;
    }
    let result = false
    for (const func of this.__moveFuncs) {
      let fnResult = func(amount);
      result = result || fnResult;
    }
    this.__allFunc();
    return result;
  }

  __scale(amount: number, event: WheelEvent) {
    if (this.__scaleFuncs.length === 0) {
      this.__allFunc();
      return false;
    }
    let result = false
    for (const func of this.__scaleFuncs) {
      let runFunc = func as any
      let fnResult = runFunc(amount, event);
      result = result || fnResult;
    }
    this.__allFunc();
    return result;
  }

  __press(pt: PointerTracker) {
    if (this.__pressedFuncs.length === 0) {
      this.__allFunc();
      return false;
    }
    const result = this.__runFuncs(this.__pressedFuncs, pt);
    this.__allFunc();
    return result;
  }



   __contextMenu(pt: PointerTracker) {
    if (this.__contextMenuFuncs.length === 0) {
      this.__allFunc();
      return false;
    }
    const result = this.__runFuncs(this.__contextMenuFuncs, pt);
    this.__allFunc();
    return result;
  }

   __release(pt: PointerTracker) {
    if (this.__releasedFuncs.length === 0) {
      this.__allFunc();
      return false;
    }
    const result = this.__runFuncs(this.__releasedFuncs, pt);
    this.__allFunc();
    return result;
  }

   __runFuncs(fns: TouchFunctionCallback[], pt: PointerTracker) {
    if (fns.length === 0) {
      return false;
    }
    let result = false;
    fns.forEach((func) => {
      const fnResult = func(pt);
      result = result || fnResult;
    })
    return result;
  }

  __hover(pt: PointerTracker) {
    if (this.__hoverFuncs.length === 0) {
      this.__allFunc();
      return false;
    }
    for (let func of this.__hoverFuncs) {
      func(pt);
    }
    this.__allFunc();
    return false;
  }

  __hoverNear(pt: PointerTracker) {
    if (this.__hoverNearFuncs.length === 0) {
      this.__allFunc();
      return false;
    }
    for (let func of this.__hoverNearFuncs) {
      func(pt);
    }
    this.__allFunc();
    return false;
  }

  __translateParentCoordToLocalCoord(pos: Vector2) {
    return pos;
    // const newCoord = new Vector2(
    //   (pos.x - this.tp_rect.l) / (this.tp_rect.r - this.tp_rect.l) * 100,
    //   (pos.y - this.tp_rect.t) / (this.tp_rect.b - this.tp_rect.t) * 100
    // )
    // return newCoord
  }

  __getPointerCenter(): Vector2 {
    const sum = new Vector2(0, 0);
    if (this.pointers.size === 0) {
      console.error("[TouchPrimitive] __getPointerCenter: Please add a pointer before calling this function.");
    }
    for (let key of Array.from(this.pointers.keys())) {
      let pos = this.pointers.get(key);
      sum.add(pos);
    }
    return new Vector2(sum.x / this.pointers.size, sum.y / this.pointers.size);
  }

  __getMeanDistanceFromPointerCenters(): number {
    let sum = 0;
    const center = this.__getPointerCenter();
    for (let key of Array.from(this.pointers.keys())) {
      let pos = this.pointers.get(key);
      const dist = center.distanceTo(pos);
      sum += dist;
    }
    return sum / this.pointers.size;
  }


  contextMenu(pointer: PointerTracker): boolean {
    let result = false;
    const ourRelPos = pointer.relPos?.clone();
    for (let child of this.tp_children.values()) {
      if (!child.intersectsPoint(pointer.relPos)) {
        continue;
      }
      this.__allFunc();
      pointer.relPos = child.__translateParentCoordToLocalCoord(
          pointer.relPos,
      );
      let result = child.contextMenu(pointer);
      if (result) return result;
    }
    if (result) return result;
    pointer.relPos = ourRelPos;
    // pointer.relPos = await this.__translateParentCoordToLocalCoord(pointer.relPos)
    // this.pointers.set(pointer.id, pointer.pos);
    this.__contextMenu(pointer);
    return true;
  }

  pointerResult: PointerResult = null

  childResult: PointerResult = null

  log (item: Object) {
    if (!this.id.startsWith("MapInputMiddleware")) {
      console.log(item)
    }
  }

  newEvent(config: {
    eventType: "pointerAdded" | "pointerChanged" | "pointerRemoved",
    pointer: PointerTracker,
    pointerOld?: PointerTracker,
    intersectsMe?: boolean,
  }): PointerResult {
    this.pointerResult = {
      consumed: false,
      consumedByChild: false,
      id: this.id,
      children: [],
      cursorToPointer: false
    }
    if (config.intersectsMe !== undefined) {
      if (!config.intersectsMe) {
        this.__hoverNear(config.pointer);
        for (let child of this.tp_children.values()) {
          child.newEvent(config)
        }
        return this.pointerResult
      }
    }
    const ourRelPos = config.pointer.relPos?.clone();
    // this.log(`HIT: newEvent ${config.eventType}, ${this.id}, ${config.intersectsMe}`)
    if (this.showPointerCursor) {
      this.pointerResult.cursorToPointer = true;
    }
    if (config.eventType === "pointerChanged") {
      this.checkForHover(config.pointer);
    }
    for (let child of this.tp_children.values()) {
      // Intersect using parent relative position, pass position relative to child to child.
      if (!child.intersectsPoint(ourRelPos)) {
        continue;
      }
      this.__allFunc();
      config.pointer.relPos = ourRelPos.clone()
      config.pointer.relPos.sub(new Vector3(
          child.x,
          child.y
      ))
      config.pointer.relPos = child.__translateParentCoordToLocalCoord(
          config.pointer.relPos,
      );
      this.childResult = child.newEvent(config);
      this.pointerResult.children.push(this.childResult);
      if (this.childResult.cursorToPointer) {
        this.pointerResult.cursorToPointer = true;
      }
      if (this.childResult.consumed) {
        this.pointerResult.consumed = true;
        this.pointerResult.consumedByChild = true;
        return this.pointerResult
      }
    }
    if (config.eventType === "pointerAdded") {
      this.pointerResult.consumed = this.pointerAdded(ourRelPos, config.pointer);
    }
    if (config.eventType === "pointerChanged") {
      this.pointerResult.consumed = this.pointerChanged(ourRelPos, config.pointer);
    }
    if (config.eventType === "pointerRemoved") {
      this.pointerResult.consumed = this.pointerRemoved(ourRelPos, config.pointer);
    }
    return this.pointerResult
  }

  debug(idStartsWith: string) {
    if (this.id.startsWith(idStartsWith)) {
      debugger;
    }
  }

  hasMoveOrScaleFuncs() {
    return this.__moveFuncs.length > 0 || this.__scaleFuncs.length > 0;
  }

  pointerAdded(ourRelPos: Vector2, pointer: PointerTracker): boolean {
      pointer.relPos = ourRelPos;
      // pointer.relPos = await this.__translateParentCoordToLocalCoord(pointer.relPos)
      let result = this.__press(pointer);
      if (result || this.hasMoveOrScaleFuncs()) {
        this.pointers.set(pointer.id, pointer.pos);
        this.resetScaleAndMove();
        result = true
      }
      return result
  }

  pointerRemoved(ourRelPos: Vector2, pointer: PointerTracker): boolean {
    let result = false
    if (this.pointers.has(pointer.id)) {
      pointer.relPos = ourRelPos;
      // pointer.relPos = await this.__translateParentCoordToLocalCoord(pointer.relPos)
      this.pointers.delete(pointer.id);
      this.resetScaleAndMove();
      if (this.pointers.size === 0) {
        this.__release(pointer);
        // TODO: I don't think pointers are released when touched on interface and dragged off interface
      }
      return true;
    }
    return result;
  }

  checkForHover(pointer: PointerTracker) {
    if (!this.pointers.has(pointer.id)) {
      if (pointer.relPos) {
        if (this.intersectsPoint(pointer.relPos)) {
          this.__hover(pointer);
        }
      } else {
        this.__hoverNear(pointer);
      }
    }
    return false;
  }

  pointerChanged(ourRelPos: Vector2, pointer: PointerTracker) {
    pointer.relPos = ourRelPos;
    if (!this.pointers.has(pointer.id)) {
      return false;
    }
    if (this.hasMoveOrScaleFuncs()) {
      this.pointers.set(pointer.id, pointer.pos);
      let scaleVal = this.__getMeanDistanceFromPointerCenters();
      let scaleAmount = scaleVal - this.__oldScalePos;
      let scaleResult = this.__scale(scaleAmount, null);
      const movePos = this.__getPointerCenter();
      const moveAmount = movePos.sub(this.__oldMovePos);
      let moveResult = this.__move(moveAmount);
      this.resetScaleAndMove();
      return true;
    }
    return false
  }

  // async pointerAdded(pointer: PointerTracker): Promise<boolean> {
  //   let result = false;
  //   const ourRelPos = pointer.relPos?.clone();
  //   for (let child of this.tp_children.values()) {
  //     if (!child.intersectsPoint(pointer.relPos)) {
  //       continue;
  //     }
  //     await this.__allFunc();
  //     pointer.relPos = await child.__translateParentCoordToLocalCoord(
  //       pointer.relPos,
  //     );
  //     let result = await child.pointerAdded(pointer);
  //     if (result) return result;
  //   }
  //   if (result) return result;
  //   pointer.relPos = ourRelPos;
  //   // pointer.relPos = await this.__translateParentCoordToLocalCoord(pointer.relPos)
  //   this.pointers.set(pointer.id, pointer.pos);
  //   this.resetScaleAndMove();
  //   await this.__press(pointer);
  //   return true;
  // }

  resetScaleAndMove() {
    if (this.pointers.size == 0) {
      return
    }
    this.__oldMovePos = this.__getPointerCenter();
    this.__oldScalePos = this.__getMeanDistanceFromPointerCenters();
  }

  // async pointerChanged(
  //   pointerOld: PointerTracker,
  //   pointer: PointerTracker,
  // ): Promise<boolean> {
  //   let result = false;
  //   const ourRelPos = pointer.relPos?.clone();
  //   for (let child of this.tp_children.values()) {
  //     pointer.relPos = await child.__translateParentCoordToLocalCoord(
  //       pointer.relPos,
  //     );
  //     let result = await child.pointerChanged(pointerOld, pointer);
  //     await this.__allFunc();
  //     if (result) return result;
  //   }
  //   pointer.relPos = ourRelPos;
  //   if (!this.pointers.has(pointer.id)) {
  //     if (pointer.relPos) {
  //       if (this.intersectsPoint(pointer.relPos)) {
  //         await this.__hover(pointer);
  //       }
  //     } else {
  //       await this.__hoverNear(pointer);
  //     }
  //     return false;
  //   }
  //   this.pointers.set(pointer.id, pointer.pos);
  //   let scaleVal = this.__getMeanDistanceFromPointerCenters();
  //   let scaleAmount = scaleVal - this.__oldScalePos;
  //   this.__scale(scaleAmount);
  //
  //   const movePos = this.__getPointerCenter();
  //   const moveAmount = movePos.sub(this.__oldMovePos);
  //   this.__move(moveAmount);
  //
  //   this.resetScaleAndMove();
  //   return true;
  // }

  // async pointerRemoved(
  //   pointer: PointerTracker,
  //   sunk: boolean,
  // ): Promise<boolean> {
  //   let result = sunk;
  //   const ourRelPos = pointer.relPos?.clone();
  //   for (let child of this.tp_children.values()) {
  //     pointer.relPos = await child.__translateParentCoordToLocalCoord(
  //       pointer.relPos,
  //     );
  //     result = (await child.pointerRemoved(pointer, sunk)) || result;
  //   }
  //   await this.__allFunc();
  //   if (this.pointers.has(pointer.id)) {
  //     pointer.relPos = ourRelPos;
  //     // pointer.relPos = await this.__translateParentCoordToLocalCoord(pointer.relPos)
  //     this.pointers.delete(pointer.id);
  //     this.resetScaleAndMove();
  //     if (this.pointers.size === 0) {
  //       await this.__release(pointer);
  //     }
  //     return true;
  //   }
  //   return false;
  // }

  scroll(event: WheelEvent): boolean {
    let result = false;
    for (let child of this.tp_children.values()) {
      let result = child.scroll(event);
      if (result) return result;
    }
    if (result) return result;
    if (event.deltaZ !== 0 && !event.ctrlKey) {
      this.__scale(event.deltaZ, event);
    }
    if (event.ctrlKey) {
      this.__scale(event.deltaY, event);
    }
    if (event.shiftKey) {
      const amount = new Vector2(-event.deltaY, 0);
      this.__move(amount.multiplyScalar(1));
    }

    if (!event.ctrlKey && !event.shiftKey) {
      // this.__scale(event.deltaY, event);
      const amount = new Vector2(-event.deltaX, -event.deltaY);
      this.__move(amount.multiplyScalar(1));
    }
    return true;
  }
}
